import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { removeCounty } from '@src/utils';
import {
  geocodeAddress,
  GEOFENCE_MESSAGE,
  isOutsideGeofence,
  LatLngLiteral,
  reverseGeocode,
  roundCoordinate,
  toLatLng,
} from './geo';
import { toastMessage } from '@utils/toastMessage';

type AddressSnapshot = {
  address1: any;
  city: any;
  state: any;
  zipCode: any;
  country: any;
  latitude: any;
  longitude: any;
};

type PendingAddress = {
  address1: any;
  city: any;
  state: any;
  zipCode: any;
  country: any;
};

type Anchor = {
  position: LatLngLiteral;
  snapshot: AddressSnapshot;
  /** false for manually entered locations: the pin may move anywhere. */
  restricted: boolean;
};

const GEOCODE_DEBOUNCE_MS = 600;
const COORDINATE_DEBOUNCE_MS = 700;
const MESSAGE_TIMEOUT_MS = 6000;
const SAME_SPOT_DEGREES = 1e-6;

const isSameSpot = (a: LatLngLiteral, b: LatLngLiteral) =>
  Math.abs(a.lat - b.lat) < SAME_SPOT_DEGREES &&
  Math.abs(a.lng - b.lng) < SAME_SPOT_DEGREES;

const stateText = (state: any) => String(state?.label || state?.value || '');

const addressKey = (
  address1: any,
  city: any,
  state: any,
  zipCode: any
): string =>
  [address1, city, stateText(state), zipCode]
    .map((part) =>
      String(part ?? '')
        .trim()
        .toLowerCase()
    )
    .join('|');

const isComplete = (
  address1: any,
  city: any,
  state: any,
  zipCode: any
): boolean =>
  Boolean(
    String(address1 ?? '').trim() &&
      String(city ?? '').trim() &&
      stateText(state).trim() &&
      /^\d{5}/.test(String(zipCode ?? '').trim())
  );

type Params = {
  overrideData: unknown;
  getValues: (name?: any) => any;
  setFormValue: (name: any, value: any, options?: any) => void;
  watch: (names: any) => any;
  states: any[];
  getStates: (arg: any) => { unwrap: () => Promise<any[]> };
};

/**
 * Owns the map pin for the Location popup.
 *
 * - `anchor` is the "original address location": the point the address resolved
 *   to when it was selected, entered or opened. Dragging the pin never changes
 *   it, so the 0.5 mile geofence is always measured from it.
 * - `syncedKeyRef` remembers which address text the pin currently matches so
 *   that programmatic field updates (pin drags, snap-backs, form resets) do not
 *   trigger a re-geocode that would reset the anchor.
 */
export const useLocationPin = ({
  overrideData,
  getValues,
  setFormValue,
  watch,
  states,
  getStates,
}: Params) => {
  const [pin, setPin] = useState<LatLngLiteral | null>(null);
  const [resetToken, setResetToken] = useState(0);
  const [message, setMessage] = useState('');
  // Address found at the dragged pin; applied only when the user asks for it.
  const [pendingAddress, setPendingAddress] = useState<PendingAddress | null>(
    null
  );

  const anchorRef = useRef<Anchor | null>(null);
  const syncedKeyRef = useRef('');
  const geocodeRequestRef = useRef(0);
  const reverseRequestRef = useRef(0);

  const [address1, city, state, zipCode] = watch([
    'address1',
    'city',
    'state',
    'zipCode',
  ]);
  const [latitude, longitude] = watch(['latitude', 'longitude']);
  const key = addressKey(address1, city, state, zipCode);

  // Latest pin for the debounced coordinate watcher below.
  const pinRef = useRef<LatLngLiteral | null>(null);
  pinRef.current = pin;

  const takeSnapshot = useCallback(
    (): AddressSnapshot => ({
      address1: getValues('address1'),
      city: getValues('city'),
      state: getValues('state'),
      zipCode: getValues('zipCode'),
      country: getValues('country'),
      latitude: getValues('latitude'),
      longitude: getValues('longitude'),
    }),
    [getValues]
  );

  /** Re-reads the form and makes its current location the new original one. */
  const seedFromValues = useCallback(
    (restricted = true) => {
      geocodeRequestRef.current += 1;
      reverseRequestRef.current += 1;
      const values = getValues();
      syncedKeyRef.current = addressKey(
        values.address1,
        values.city,
        values.state,
        values.zipCode
      );
      const position = toLatLng(values.latitude, values.longitude);
      anchorRef.current = position
        ? { position, snapshot: takeSnapshot(), restricted }
        : null;
      setPin(position);
      setMessage('');
      setPendingAddress(null);
    },
    [getValues, takeSnapshot]
  );

  // Saved location or suggested address opened with the popup. A popup opened
  // without one is a manual entry, which is not geofenced.
  useEffect(() => {
    seedFromValues(Boolean(overrideData));
  }, [overrideData]);

  // Manually entered / edited address: resolve it and move the pin.
  useEffect(() => {
    if (key === syncedKeyRef.current) return undefined;

    const timer = setTimeout(async () => {
      syncedKeyRef.current = key;
      const requestId = ++geocodeRequestRef.current;

      if (!isComplete(address1, city, state, zipCode)) {
        anchorRef.current = null;
        setPin(null);
        return;
      }

      const position = await geocodeAddress({
        address1: String(address1).trim(),
        city: String(city).trim(),
        state: stateText(state),
        zipCode: String(zipCode).trim(),
      });
      if (requestId !== geocodeRequestRef.current) return;

      if (!position) {
        anchorRef.current = null;
        setPin(null);
        return;
      }

      setFormValue('latitude', position.lat, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setFormValue('longitude', position.lng, {
        shouldDirty: true,
        shouldValidate: true,
      });
      anchorRef.current = {
        position,
        snapshot: takeSnapshot(),
        restricted: Boolean(overrideData),
      };
      setMessage('');
      setPendingAddress(null);
      setPin(position);
    }, GEOCODE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [key]);

  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(''), MESSAGE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [message]);

  const applySnapshot = useCallback(
    (snapshot: AddressSnapshot) => {
      syncedKeyRef.current = addressKey(
        snapshot.address1,
        snapshot.city,
        snapshot.state,
        snapshot.zipCode
      );
      (Object.keys(snapshot) as (keyof AddressSnapshot)[]).forEach((name) =>
        setFormValue(name, snapshot[name], {
          shouldDirty: true,
          shouldValidate: true,
        })
      );
    },
    [setFormValue]
  );

  const writeAddress = useCallback(
    (address: PendingAddress) => {
      syncedKeyRef.current = addressKey(
        address.address1,
        address.city,
        address.state,
        address.zipCode
      );
      const options = { shouldDirty: true, shouldValidate: true };
      (Object.keys(address) as (keyof PendingAddress)[]).forEach((name) =>
        setFormValue(name, address[name], options)
      );
    },
    [setFormValue]
  );

  const handlePinMove = useCallback(
    async (next: LatLngLiteral) => {
      const anchor = anchorRef.current;
      reverseRequestRef.current += 1;
      const requestId = reverseRequestRef.current;

      if (anchor?.restricted && isOutsideGeofence(next, anchor.position)) {
        applySnapshot(anchor.snapshot);
        setPin(anchor.position);
        setResetToken((token) => token + 1);
        setPendingAddress(null);
        setMessage(GEOFENCE_MESSAGE);
        return;
      }

      const position = {
        lat: roundCoordinate(next.lat),
        lng: roundCoordinate(next.lng),
      };
      setMessage('');
      setPendingAddress(null);
      setPin(position);
      setFormValue('latitude', position.lat, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setFormValue('longitude', position.lng, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const address = await reverseGeocode(position);
      if (requestId !== reverseRequestRef.current) return;
      if (!address) {
        toast.warn(toastMessage.PLACE.addressNotFoundAtPin);
        return;
      }

      let stateValue = getValues('state');
      const abbreviation = address.state.toUpperCase();
      if (abbreviation) {
        let options = states;
        if (!options.length) {
          try {
            options = await getStates({}).unwrap();
          } catch {
            options = [];
          }
          if (requestId !== reverseRequestRef.current) return;
        }
        stateValue = options.find(
          (item) => item.label?.toUpperCase() === abbreviation
        ) ?? { id: null, label: abbreviation, value: abbreviation };
      }

      const countyValue = address.county
        ? {
            id: null,
            label: removeCounty(address.county),
            value: address.county,
          }
        : getValues('country');

      const nextAddress1 = address.address1 || getValues('address1');
      const nextCity = address.city || getValues('city');
      const nextZip = address.zipCode || getValues('zipCode');

      const found: PendingAddress = {
        address1: nextAddress1,
        city: nextCity,
        state: stateValue,
        zipCode: nextZip,
        country: countyValue,
      };
      if (overrideData) {
        setPendingAddress(found);
      } else {
        writeAddress(found);
      }
      // Address Line 2 is intentionally left as entered.
    },
    [
      applySnapshot,
      getStates,
      getValues,
      overrideData,
      setFormValue,
      states,
      writeAddress,
    ]
  );

  const applyPendingAddress = useCallback(() => {
    if (!pendingAddress) return;
    writeAddress(pendingAddress);
    setPendingAddress(null);
  }, [pendingAddress, writeAddress]);

  // Typed / pasted coordinates: once the user stops typing, put the pin on
  // that exact spot. Updates the pin itself caused (drags, geocoding, snap
  // backs) land on the pin already and are skipped.
  useEffect(() => {
    const next = toLatLng(latitude, longitude);
    if (!next) return undefined;
    if (pinRef.current && isSameSpot(pinRef.current, next)) return undefined;

    const timer = setTimeout(() => {
      if (pinRef.current && isSameSpot(pinRef.current, next)) return;
      // Typed coordinates win over any lookup still in flight.
      geocodeRequestRef.current += 1;
      reverseRequestRef.current += 1;

      const anchor = anchorRef.current;
      if (anchor?.restricted && isOutsideGeofence(next, anchor.position)) {
        applySnapshot(anchor.snapshot);
        setPin(anchor.position);
        setResetToken((token) => token + 1);
        setPendingAddress(null);
        setMessage(GEOFENCE_MESSAGE);
        return;
      }

      setMessage('');
      setPin(next);
      if (!anchor) {
        anchorRef.current = {
          position: next,
          snapshot: takeSnapshot(),
          restricted: false,
        };
      }
    }, COORDINATE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [latitude, longitude]);

  return {
    pin,
    resetToken,
    message,
    seedFromValues,
    handlePinMove,
    hasPendingAddress: Boolean(pendingAddress),
    applyPendingAddress,
  };
};
